import { curveSegment } from "../../MobileCapabilityLineChart"
import type { StripPlotArea } from "../stripLayout"
import type { TimeValuePoint } from "../interpolate"

// iOS Safari silently drops SVG paths whose geometry extends far outside
// the viewport, and pre-choice the future segment does exactly that: on
// the frozen linear axis its endpoint computes to y ≈ -100k px, so the
// whole black projection vanished on iPhones. Cap every screen point to
// a small window around the strip before building paths. The cap sits
// beyond the svg's own clip, so the visible line (including its designed
// exit through the strip's top edge) is unchanged.
const LINE_RENDER_OVERFLOW_PX = 48

// time/value → capped screen coordinates for the current frame.
export type LineProjection = {
  xForTime: (time: number) => number
  yForValue: (value: number) => number
  capX: (x: number) => number
  capY: (y: number) => number
}

export const makeLineProjection = (
  plot: StripPlotArea,
  xForTime: (time: number) => number,
  yForValue: (value: number) => number,
): LineProjection => ({
  xForTime,
  yForValue,
  capX: x =>
    Math.min(Math.max(x, -LINE_RENDER_OVERFLOW_PX), plot.width + LINE_RENDER_OVERFLOW_PX),
  capY: y =>
    Math.min(Math.max(y, -LINE_RENDER_OVERFLOW_PX), plot.height + LINE_RENDER_OVERFLOW_PX),
})

// One drawable stretch of a capability series.
export type LineSpan = {
  fromT: number
  toT: number
  floorValue: number
  topClampY?: number | null
  series: TimeValuePoint[]
  valueAt: (time: number) => number
}

// Screen-space control points over [fromT, toT]: the span's endpoints
// plus every series point strictly between them.
export const buildLineScreenPoints = (
  proj: LineProjection,
  span: LineSpan,
): Array<[number, number]> =>
  spanTimestamps(span).map(t => projectPoint(proj, span, t))

// Smooth svg path through the span's control points.
export const buildLinePath = (proj: LineProjection, span: LineSpan): string => {
  const screen = buildLineScreenPoints(proj, span)
  let d = `M ${screen[0][0]} ${screen[0][1]}`
  for (let i = 0; i < screen.length - 1; i++) {
    d += curveSegment(screen[i], screen[i + 1])
  }
  return d
}

const spanTimestamps = (span: LineSpan): number[] => {
  const timestamps: number[] = [span.fromT]
  for (const point of span.series) {
    if (point.time > span.fromT && point.time < span.toT) timestamps.push(point.time)
  }
  timestamps.push(span.toT)
  return timestamps
}

const projectPoint = (
  proj: LineProjection,
  span: LineSpan,
  t: number,
): [number, number] => {
  const y = proj.yForValue(Math.max(span.valueAt(t), span.floorValue))
  const topClampY = span.topClampY ?? null
  return [
    proj.capX(proj.xForTime(t)),
    proj.capY(topClampY !== null ? Math.max(y, topClampY) : y),
  ]
}
